import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { 
  fieldDependencies,
  getFieldMax,
  getFieldMin,
  getFieldStep,
  getFieldValidationAttributes,
  isFieldRequired,
  isNumericField,
  shouldShowField,
  useFormValidation,
  validationRules,
} from '../utils/formValidation'

export const useRealTimeValidation = (formData, isDirty = false, deviceInfo = null) => {
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [previouslyVisibleFields, setPreviouslyVisibleFields] = useState(new Set())
  const errorsRef = useRef({})
  
  // Keep errors ref in sync
  useEffect(() => {
    errorsRef.current = errors
  }, [errors])
  
  // Memoize validation functions
  const { validateField, validateForm, validateConditionalFields } = useFormValidation()
  
  // Memoize all possible field names to avoid hardcoding
  const allPossibleFields = useMemo(() => new Set([
    ...Object.keys(validationRules),
    ...Object.keys(formData),
  ]), [formData])

  // Validate individual field on blur
  const validateSingleField = useCallback((fieldName, value) => {
    // ABSOLUTE RULE: If field is not visible, no validation error
    if (!shouldShowField(fieldName, formData, deviceInfo)) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldName]
        return newErrors
      })
      return
    }
    
    const error = validateField(fieldName, value, formData)
    setErrors(prev => ({
      ...prev,
      [fieldName]: error ? { message: error } : undefined,
    }))
  }, [validateField, formData])

  // Mark field as touched
  const touchField = useCallback((fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }))
  }, [])
  // Validate all fields with enhanced validation
  const validateAllFields = useCallback(() => {
    const newErrors = validateForm(formData)
    
    // Add additional conditional field validation
    const conditionalErrors = validateConditionalFields(formData)
    
    // Combine errors and filter for visible fields in one pass
    const allErrors = { ...newErrors, ...conditionalErrors }
    
    const visibleErrors = Object.fromEntries(
      Object.entries(allErrors).filter(([fieldName]) => {
        return shouldShowField(fieldName, formData, deviceInfo)
      }),
    )
    
    // ABSOLUTE RULE: Remove any errors for invisible fields
    setErrors(_prevErrors => {
      const cleanedErrors = Object.fromEntries(
        Object.entries(visibleErrors).filter(([fieldName]) => {
          return shouldShowField(fieldName, formData, deviceInfo)
        }),
      )
      return cleanedErrors
    })
    
    return Object.keys(visibleErrors).length === 0
  }, [validateForm, validateConditionalFields, formData])

  // Helper function to process field validation
  const processFieldValidation = useCallback((fieldName, currentErrors, formData) => {
    const isVisible = shouldShowField(fieldName, formData, deviceInfo)
    const error = isVisible ? validateField(fieldName, formData[fieldName], formData) : null
    const newError = error ? { message: error } : undefined
    
    const hadError = !!currentErrors[fieldName]
    const willHaveError = !!newError
    const isNewlyVisible = isVisible && !previouslyVisibleFields.has(fieldName) && validationRules[fieldName]
    
    const hasChanged = hadError !== willHaveError || 
                      (hadError && currentErrors[fieldName]?.message !== newError?.message) ||
                      (isNewlyVisible && newError)
    
    return { isVisible, newError, hasChanged }
  }, [validateField, previouslyVisibleFields])

  // Consolidated effect for form data changes with optimized validation
  useEffect(() => {
    if (!isDirty) return
    
    const updateValidationState = () => {
      const currentErrors = errorsRef.current
      const fieldsToProcess = new Set([...allPossibleFields, ...Object.keys(currentErrors)])
      const currentlyVisibleFields = new Set()
      const newErrors = {}
      let hasChanges = false
      
      // Process all fields in a single optimized pass
      for (const fieldName of fieldsToProcess) {
        const { isVisible, newError, hasChanged } = processFieldValidation(fieldName, currentErrors, formData)
        
        if (isVisible) {
          currentlyVisibleFields.add(fieldName)
          if (newError) newErrors[fieldName] = newError
        }
        
        if (hasChanged) hasChanges = true
      }
      
      // Update errors if there are changes
      if (hasChanges) {
        setErrors(newErrors)
      }
      
      // Update visible fields separately to avoid state update loops
      setPreviouslyVisibleFields(prev => {
        const hasVisibilityChanges = prev.size !== currentlyVisibleFields.size || 
          [...currentlyVisibleFields].some(field => !prev.has(field))
        return hasVisibilityChanges ? currentlyVisibleFields : prev
      })
    }
    
    // Use requestAnimationFrame for better performance than setTimeout
    const frameId = requestAnimationFrame(updateValidationState)
    return () => cancelAnimationFrame(frameId)
  }, [formData, isDirty, allPossibleFields, processFieldValidation])

  // Memoized computed values for performance
  const validationState = useMemo(() => {
    const errorKeys = Object.keys(errors).filter(key => errors[key])
    return {
      isValid: errorKeys.length === 0,
      errorCount: errorKeys.length,
      errorFields: errorKeys,
    }
  }, [errors])

  // Consolidated field utilities with memoization
  const fieldUtils = useMemo(() => ({
    getFieldError: (fieldName) => touched[fieldName] ? errors[fieldName] : undefined,
    isFieldVisible: (fieldName) => shouldShowField(fieldName, formData, deviceInfo),
    isFieldDynamicallyRequired: (fieldName) => isFieldRequired(fieldName, formData),
    getValidationAttributes: (fieldName) => getFieldValidationAttributes(fieldName, formData),
    getFieldDependencies: (fieldName) => ({ showWhen: fieldDependencies.showWhenValue[fieldName] }),
    getFieldRules: (fieldName) => validationRules[fieldName],
    getFieldInputAttributes: (fieldName) => ({
      step: getFieldStep(fieldName),
      min: getFieldMin(fieldName),
      max: getFieldMax(fieldName),
      isNumeric: isNumericField(fieldName),
    }),
  }), [touched, errors, formData])

  // Helper functions for error scrolling (broken down for clarity)
  const getFieldSelectors = useCallback((fieldName) => [
    `[name="${fieldName}"]`,
    `#${fieldName}`,
    `[data-field="${fieldName}"]`,
    `.form-group [name="${fieldName}"]`,
    `.mb-3 [name="${fieldName}"]`,
  ], [])

  const findFirstErrorElement = useCallback((errorFields) => {
    return errorFields
      .flatMap(fieldName => getFieldSelectors(fieldName)
        .map(selector => document.querySelector(selector))
        .filter(Boolean)
        .map(element => ({ 
          element, 
          position: element.getBoundingClientRect().top + window.pageYOffset, 
        })),
      )
      .sort((a, b) => a.position - b.position)[0]?.element
  }, [getFieldSelectors])

  const scrollAndHighlightElement = useCallback((element) => {
    window.scrollTo({ 
      top: element.getBoundingClientRect().top + window.pageYOffset - 120, 
      behavior: 'smooth', 
    })
    
    setTimeout(() => {
      element.focus()
      const originalBorder = element.style.border
      element.style.cssText += 'border: 2px solid #dc3545; transition: border 0.3s ease;'
      setTimeout(() => element.style.border = originalBorder, 2000)
    }, 500)
  }, [])

  const cleanupPhantomErrors = useCallback((errorFields) => {
    const presentSelectors = new Set(
      errorFields.flatMap(getFieldSelectors)
        .filter(selector => document.querySelector(selector)),
    )
    
    const phantomFields = errorFields.filter(fieldName => 
      !getFieldSelectors(fieldName).some(selector => presentSelectors.has(selector)),
    )
    
    if (phantomFields.length) {
      setErrors(prevErrors => 
        Object.fromEntries(Object.entries(prevErrors).filter(([key]) => !phantomFields.includes(key))),
      )
    }
  }, [getFieldSelectors])

  // Main scroll to error function (now simplified)
  const scrollToFirstError = useCallback(() => {
    const visibleErrorFields = Object.keys(errors).filter(fieldName => 
      errors[fieldName] && shouldShowField(fieldName, formData, deviceInfo),
    )
    
    if (!visibleErrorFields.length) return

    const firstElement = findFirstErrorElement(visibleErrorFields)
    
    if (firstElement) {
      scrollAndHighlightElement(firstElement)
    } else {
      cleanupPhantomErrors(visibleErrorFields)
    }
  }, [errors, formData, findFirstErrorElement, scrollAndHighlightElement, cleanupPhantomErrors])

  // Reset validation state
  const resetValidation = useCallback(() => {
    setErrors({})
    setTouched({})
  }, [])

  return {
    // Core validation state
    errors,
    touched,
    isValid: validationState.isValid,
    errorCount: validationState.errorCount,
    
    // Validation actions
    validateSingleField,
    touchField,
    validateAllFields,
    resetValidation,
    scrollToFirstError,
    
    // Field utilities (consolidated for better organization)
    ...fieldUtils,
    
    // Computed getters
    getErrorFields: () => validationState.errorFields,
  }
}

export default useRealTimeValidation
